import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import ReactMarkdown from 'react-markdown';
import { 
  ArrowLeft, Play, CheckCircle, Circle, Clock, Lock, 
  Loader2, ChevronLeft, ChevronRight, Menu, X,
  BookOpen, Video, ShieldAlert
} from 'lucide-react';
import Header from '../components/Header';
import '../styles/CoursePlayer.css';


// Helper function to detect video type and extract ID
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // Vimeo URL patterns
  const vimeoRegex = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/;
  const vimeoMatch = url.match(vimeoRegex);
  if (vimeoMatch) {
    const videoId = vimeoMatch[1];
    // Vimeo embed with privacy settings - dnt=1 disables tracking, 
    // autopause=0 allows multiple embeds, and we add domain restriction params
    return {
      type: 'vimeo',
      url: `https://player.vimeo.com/video/${videoId}?dnt=1&autopause=0&title=0&byline=0&portrait=0`,
      id: videoId
    };
  }
  
  // YouTube URL patterns  
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const youtubeMatch = url.match(youtubeRegex);
  if (youtubeMatch) {
    const videoId = youtubeMatch[1];
    return {
      type: 'youtube',
      url: `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`,
      id: videoId
    };
  }
  
  // Direct Vimeo embed code - extract src
  if (url.includes('player.vimeo.com')) {
    return {
      type: 'vimeo',
      url: url.includes('?') ? url : `${url}?dnt=1&title=0&byline=0&portrait=0`,
      id: 'embedded'
    };
  }
  
  return null;
};

const CoursePlayer = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchCourseContent();
  }, [slug]);

  const fetchCourseContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/my-courses/${slug}`, {
        withCredentials: true
      });
      setCourse(response.data);
      
      // Set first incomplete module as current, or first module
      const firstIncomplete = response.data.modules.find(m => !m.completed);
      setCurrentModule(firstIncomplete || response.data.modules[0]);
      
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('access_denied');
      } else if (err.response?.status === 401) {
        navigate('/login', { state: { from: { pathname: `/my-courses/${slug}` } } });
        return;
      } else {
        setError('failed');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleModuleSelect = (module) => {
    setCurrentModule(module);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!currentModule) return;
    
    try {
      await axios.post(
        `${API_URL}/api/my-courses/${slug}/progress`,
        {
          module_id: currentModule.id,
          completed: true,
          last_position_seconds: 0
        },
        { withCredentials: true }
      );
      
      // Update local state
      setCourse(prev => ({
        ...prev,
        modules: prev.modules.map(m => 
          m.id === currentModule.id ? { ...m, completed: true } : m
        )
      }));
      setCurrentModule(prev => ({ ...prev, completed: true }));
      
      // Auto-advance to next module
      const currentIndex = course.modules.findIndex(m => m.id === currentModule.id);
      if (currentIndex < course.modules.length - 1) {
        setCurrentModule(course.modules[currentIndex + 1]);
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const goToPrevModule = () => {
    const currentIndex = course.modules.findIndex(m => m.id === currentModule.id);
    if (currentIndex > 0) {
      setCurrentModule(course.modules[currentIndex - 1]);
    }
  };

  const goToNextModule = () => {
    const currentIndex = course.modules.findIndex(m => m.id === currentModule.id);
    if (currentIndex < course.modules.length - 1) {
      setCurrentModule(course.modules[currentIndex + 1]);
    }
  };

  const calculateProgress = () => {
    if (!course?.modules?.length) return 0;
    const completed = course.modules.filter(m => m.completed).length;
    return Math.round((completed / course.modules.length) * 100);
  };

  if (loading) {
    return (
      <div className="course-player-page">
        <Header />
        <div className="player-loading">
          <Loader2 size={48} className="spinner" />
          <p>Loading course content...</p>
        </div>
      </div>
    );
  }

  if (error === 'access_denied') {
    return (
      <div className="course-player-page">
        <Header />
        <div className="access-denied">
          <Lock size={64} />
          <h2>Access Denied</h2>
          <p>You don't have access to this course. Please purchase it first.</p>
          <Link to={`/courses/${slug}`} className="btn-purchase-now">
            View Course Details
          </Link>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-player-page">
        <Header />
        <div className="player-error">
          <h2>Something went wrong</h2>
          <p>Failed to load course content.</p>
          <Link to="/dashboard" className="btn-back">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const currentIndex = course.modules.findIndex(m => m.id === currentModule?.id);
  const progress = calculateProgress();

  return (
    <div className="course-player-page">
      {/* Minimal Header for Course Player */}
      <header className="player-header">
        <Link to="/dashboard" className="back-to-dashboard">
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </Link>
        
        <div className="course-title-header">
          <h1>{course.title}</h1>
        </div>
        
        <button 
          className="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      <div className="player-container">
        {/* Sidebar - Module List */}
        <aside className={`player-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h2>Course Content</h2>
            <div className="progress-indicator">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span>{progress}% complete</span>
            </div>
          </div>
          
          <div className="modules-list">
            {course.modules.map((module, index) => (
              <button
                key={module.id}
                className={`module-item ${currentModule?.id === module.id ? 'active' : ''} ${module.completed ? 'completed' : ''}`}
                onClick={() => handleModuleSelect(module)}
              >
                <div className="module-status">
                  {module.completed ? (
                    <CheckCircle size={20} className="completed-icon" />
                  ) : (
                    <Circle size={20} />
                  )}
                </div>
                <div className="module-content">
                  <span className="module-number">{index + 1}</span>
                  <span className="module-title">{module.title}</span>
                </div>
                <div className="module-duration">
                  <Clock size={14} />
                  {module.duration_minutes}m
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="player-main">
          {currentModule ? (
            <>
              {/* Video Player - Protected */}
              <div className="video-container" onContextMenu={(e) => e.preventDefault()}>
                {currentModule.video_url ? (
                  <div className="video-wrapper protected-video">
                    {(() => {
                      const videoData = getVideoEmbedUrl(currentModule.video_url);
                      if (videoData) {
                        return (
                          <>
                            <iframe
                              src={videoData.url}
                              title={currentModule.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                              allowFullScreen
                              referrerPolicy="strict-origin"
                            />
                            {/* Invisible overlay to prevent right-click and URL inspection */}
                            <div className="video-protection-overlay" />
                          </>
                        );
                      }
                      return (
                        <div className="video-placeholder">
                          <ShieldAlert size={64} />
                          <p>Invalid video URL</p>
                          <span>Please contact support if this issue persists.</span>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="video-placeholder">
                    <Video size={64} />
                    <p>Video coming soon</p>
                    <span>The video for this module hasn't been uploaded yet.</span>
                  </div>
                )}
              </div>

              {/* Module Info & Navigation */}
              <div className="module-header">
                <div className="module-info">
                  <span className="module-label">
                    Module {currentIndex + 1} of {course.modules.length}
                  </span>
                  <h2>{currentModule.title}</h2>
                </div>
                
                <div className="module-actions">
                  {!currentModule.completed && (
                    <button 
                      className="btn-mark-complete"
                      onClick={handleMarkComplete}
                    >
                      <CheckCircle size={20} />
                      Mark Complete
                    </button>
                  )}
                  {currentModule.completed && (
                    <span className="completed-badge">
                      <CheckCircle size={18} />
                      Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="module-navigation">
                <button 
                  className="nav-btn prev"
                  onClick={goToPrevModule}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft size={20} />
                  Previous
                </button>
                
                <button 
                  className="nav-btn next"
                  onClick={goToNextModule}
                  disabled={currentIndex === course.modules.length - 1}
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Notes Section */}
              {currentModule.notes_markdown && (
                <div className="notes-section">
                  <div className="notes-header">
                    <BookOpen size={20} />
                    <h3>Module Notes</h3>
                  </div>
                  <div className="notes-content">
                    <ReactMarkdown>{currentModule.notes_markdown}</ReactMarkdown>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-module-selected">
              <p>Select a module from the sidebar to begin</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CoursePlayer;
